import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useSessionKickedStore } from '@/store/auth';
import type { UserBalanceDto } from '@/api/user';

// Generic WS payload — every message carries a `type` field; everything else
// is variant-specific.
export type WsMessage = { type: string; [k: string]: unknown };

type Ctx = {
  /** True once the socket has reached OPEN. Flips back to false on close. */
  connected: boolean;
  /** Fire-and-forget send. Returns false if the socket isn't open yet. */
  send: (msg: WsMessage) => boolean;
  /** Subscribe a listener for inbound messages. Returns an unsubscribe fn. */
  addMessageHandler: (fn: (msg: unknown) => void) => () => void;
};

const SocketContext = createContext<Ctx | null>(null);

// Derive the WS host from VITE_API_BASE_URL. In dev with no base URL set,
// fall back to the page origin (same host the SPA is served from).
function wsBaseUrl(token: string | null): string | null {
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const host = apiBase
    ? apiBase.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    : typeof window !== 'undefined'
      ? window.location.host
      : '';
  if (!host) return null;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const proto = apiBase.startsWith('https://') || (apiBase === '' && secure) ? 'wss' : 'ws';
  const base = `${proto}://${host}/ws/subscribe`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

// After this many consecutive connection attempts that die without ever
// reaching OPEN (404 from the gateway, refused, bad proxy), the endpoint is
// treated as down and retries drop to minute-level. Attempts that connected
// at least once reset the counter.
const INSTANT_FAIL_LIMIT = 5;

// Single long-lived WS connection mirroring bestmain-web's WebSocketProvider:
//   - opens against /ws/subscribe?token=<jwt> when the user is authed
//   - re-authes via JSON if the server didn't accept the query param
//   - subscribes to the 'balance' topic so cash/bonus updates land in cache
//     without a poll
//   - keeps alive via ping/pong
//   - reconnects with jittered exponential backoff up to 15s; if the
//     endpoint looks dead (INSTANT_FAIL_LIMIT handshake failures in a row)
//     retries stretch to 1-2 min so a broken gateway route doesn't turn the
//     user base into a reconnect storm
//   - pauses reconnects while the tab is hidden; resumes on visibilitychange
//
// The provider is mounted at the app root via main.tsx so any view (and any
// future feature, not just hash games) can call useHashGameSocket() and join.
export function HashGameSocketProvider({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1000);
  const instantFailsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscribedRef = useRef(false);
  const listenersRef = useRef(new Set<(msg: unknown) => void>());
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token) {
      // Logged out — tear down any live connection so we don't keep sending
      // messages with a stale Authorization context.
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
      setConnected(false);
      return;
    }

    let closedByUser = false;
    let resumeOnVisible = false;
    const url = wsBaseUrl(token);
    if (!url) return;

    const connect = () => {
      if (closedByUser) return;
      // Hidden tabs don't need live pushes — defer the attempt and let the
      // visibilitychange handler re-arm it when the user comes back.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        resumeOnVisible = true;
        return;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Close any stale instance before replacing it so onclose handlers from
      // the old socket don't schedule overlapping reconnects.
      if (wsRef.current) {
        try {
          wsRef.current.onopen = null;
          wsRef.current.onmessage = null;
          wsRef.current.onclose = null;
          wsRef.current.onerror = null;
          wsRef.current.close();
        } catch {
          // ignore
        }
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;
      subscribedRef.current = false;
      let sawOpen = false;

      ws.onopen = () => {
        sawOpen = true;
        instantFailsRef.current = 0;
        setConnected(true);
        backoffRef.current = 1000;
        if (!subscribedRef.current) {
          try {
            // Subscribe to the global balance topic. Per-game hash subscribes
            // come from useHashGameSubscription so unmounting a view also
            // unsubscribes that game.
            ws.send(JSON.stringify({ action: 'subscribe', topics: ['balance'] }));
            subscribedRef.current = true;
          } catch {
            // ignore — onerror/onclose will surface the underlying issue
          }
        }
      };

      const handleMessage = (raw: unknown) => {
        // Server sometimes pings as a string ("ping") and sometimes as an
        // object — handle both.
        const isPing =
          (typeof raw === 'string' && raw.toLowerCase() === 'ping') ||
          (typeof raw === 'object' &&
            raw !== null &&
            ((raw as { type?: string }).type === 'ping' ||
              (raw as { event?: string }).event === 'ping'));
        if (isPing) {
          try {
            ws.send(JSON.stringify({ type: 'pong', t: Date.now() }));
          } catch {
            // ignore
          }
          return;
        }

        // Session replaced — another device took over this account. Stop
        // reconnecting (closedByUser prevents the onclose handler from
        // scheduling a retry) and show the popup. Auth is cleared only when
        // the user dismisses, so they stay on the current page until then.
        if (
          typeof raw === 'object' &&
          raw !== null &&
          (raw as Record<string, unknown>).code === 4021 &&
          (raw as Record<string, unknown>).type === 'session_replaced'
        ) {
          closedByUser = true;
          try {
            ws.close();
          } catch {
            // ignore
          }
          const r = raw as Record<string, unknown>;
          useSessionKickedStore.getState().setKicked({
            country: String(r.country ?? ''),
            ip: String(r.ip ?? ''),
            login_time: String(r.login_time ?? ''),
          });
          return;
        }

        // Unauthenticated → server will close shortly; let the close handler
        // schedule reconnect (which will be a no-op until token changes).
        if (
          typeof raw === 'object' &&
          raw !== null &&
          ((raw as { message?: string }).message === 'Unauthenticated' ||
            (raw as { error?: string }).error === 'Unauthenticated')
        ) {
          closedByUser = true;
          try {
            ws.close();
          } catch {
            // ignore
          }
          return;
        }

        // Balance push — keep TanStack Query's user/balance entry in sync so
        // every consumer (header, deposit page, bet flow) sees the new total
        // without a refetch round-trip.
        if (typeof raw === 'object' && raw !== null) {
          const r = raw as Record<string, unknown>;
          const notif = (r.data as Record<string, unknown> | undefined)?.notification as
            | Record<string, unknown>
            | undefined;
          const balance = (notif?.balance as Record<string, unknown> | undefined) ?? undefined;
          if (balance && (balance.cash !== undefined || balance.bonus !== undefined)) {
            qc.setQueryData<UserBalanceDto>(['user', 'balance'], (prev) => {
              if (!prev) return prev;
              const next: UserBalanceDto = { ...prev };
              if (balance.cash !== undefined) next.cash_balance = Number(balance.cash);
              if (balance.bonus !== undefined) next.bonus_balance = Number(balance.bonus);
              if (balance.xp !== undefined) next.xp = Number(balance.xp);
              if (balance.vip !== undefined) next.vip = Number(balance.vip);
              return next;
            });
          }
        }

        // Fan out to feature-specific listeners (hash-game subscriptions,
        // place_bet_game ack waiters, etc.).
        listenersRef.current.forEach((fn) => {
          try {
            fn(raw);
          } catch {
            // listener errors are isolated so one bad consumer doesn't kill
            // the rest of the fanout
          }
        });
      };

      ws.onmessage = (ev) => {
        const data = ev.data;
        if (typeof data === 'string') {
          try {
            handleMessage(JSON.parse(data));
          } catch {
            handleMessage(data);
          }
        } else if (data && typeof (data as Blob).text === 'function') {
          (data as Blob)
            .text()
            .then((text) => {
              try {
                handleMessage(JSON.parse(text));
              } catch {
                handleMessage(text);
              }
            })
            .catch(() => {
              // ignore
            });
        } else {
          handleMessage(data);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        subscribedRef.current = false;
        if (closedByUser) return;
        if (!sawOpen) instantFailsRef.current += 1;
        let delay: number;
        if (instantFailsRef.current >= INSTANT_FAIL_LIMIT) {
          // Endpoint looks dead — exponential backoff alone would still hit
          // the gateway every 15s forever. Stretch to 1-2 min (randomized so
          // clients stay desynchronized) until a connect succeeds.
          delay = 60_000 + Math.random() * 60_000;
        } else {
          const base = Math.min(backoffRef.current, 15_000);
          backoffRef.current *= 2;
          // Up to +50% jitter so a server restart doesn't get the whole
          // user base reconnecting in lockstep.
          delay = base + Math.random() * (base / 2);
        }
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !resumeOnVisible) return;
      resumeOnVisible = false;
      connect();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    connect();
    return () => {
      closedByUser = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, [token, qc]);

  // Stable references via empty-deps useCallback — the bodies only touch
  // refs, so they never need to "see" fresh state. Without this, the WS
  // reconnect that flips `connected` would mint new function refs and force
  // every useHashGameSubscription effect to unsubscribe+resubscribe.
  const send = useCallback((msg: WsMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }, []);

  const addMessageHandler = useCallback((fn: (msg: unknown) => void) => {
    listenersRef.current.add(fn);
    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  return (
    <SocketContext.Provider value={{ connected, send, addMessageHandler }}>
      {children}
    </SocketContext.Provider>
  );
}

// Component consumer. Returns a no-op context if the provider isn't mounted —
// keeps unit tests and storybooks crash-free.
export function useHashGameSocket(): Ctx {
  const ctx = useContext(SocketContext);
  if (ctx) return ctx;
  return {
    connected: false,
    send: () => false,
    addMessageHandler: () => () => undefined,
  };
}
